import React, { useMemo, useState } from "react";
import {
  Autocomplete,
  Chip,
  Stack,
  SxProps,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import { useContext } from "react";
import { AppContext } from "../../AppContextProvider";
import {
  DEFAULT_RFQ_STATUS_OPTIONS,
  getRFQStatusLabel,
  getRfqStatusColor,
  RFQStatus,
} from "../../models/RFQStatus";
import { copyTextToClipboard } from "../../utils/copyTextToClipboard";
import ConfirmDialog from "../ConfirmDialog";

export interface RfqStatusAutocompleteProps {
  status: RFQStatus | string;
  onStatusChange: (status: RFQStatus) => void | Promise<void>;
  label?: string;
  hideCaption?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  skipConfirm?: boolean;
  /** Focus the input on mount (work-queue click-to-edit). */
  autoFocus?: boolean;
  /** Exit click-to-edit when the popup closes without a pending confirm. */
  onBlurExit?: () => void;
}

const RfqStatusAutocomplete: React.FC<RfqStatusAutocompleteProps> = ({
  status,
  onStatusChange,
  label = "Current Status",
  hideCaption = false,
  disabled = false,
  sx,
  skipConfirm = false,
  autoFocus = false,
  onBlurExit,
}) => {
  const { rfqStatuses = [] } = useContext(AppContext);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);

  const options = useMemo(() => {
    const catalogEntries = rfqStatuses.filter((t) => t.value !== RFQStatus.NEW);
    const merged: string[] = [];
    const seen = new Set<string>();

    if (catalogEntries.length > 0) {
      const sorted = [...catalogEntries].sort(
        (a, b) =>
          a.displayOrder - b.displayOrder ||
          a.label.localeCompare(b.label)
      );
      for (const entry of sorted) {
        if (!seen.has(entry.value)) {
          seen.add(entry.value);
          merged.push(entry.value);
        }
      }
    }

    // Include built-in statuses (e.g. ORDER) even before catalog sync/migration.
    for (const value of DEFAULT_RFQ_STATUS_OPTIONS) {
      if (!seen.has(value)) {
        seen.add(value);
        merged.push(value);
      }
    }

    if (status && !seen.has(status)) {
      return [status, ...merged];
    }
    return merged;
  }, [rfqStatuses, status]);

  const statusLabel = getRFQStatusLabel(status, rfqStatuses);
  const copyEnabled = statusLabel.length > 0;
  const pendingLabel = pendingStatus
    ? getRFQStatusLabel(pendingStatus, rfqStatuses)
    : "";
  const colors = getRfqStatusColor(status, rfqStatuses);

  const commitStatus = (next: string) => {
    void onStatusChange(next as RFQStatus);
  };

  return (
    <Stack sx={{ minWidth: 0, ...sx }}>
      {!hideCaption && (
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      )}
      <Autocomplete
        size="small"
        disabled={disabled}
        autoHighlight={true}
        disableClearable
        open={autoFocus ? true : undefined}
        options={options}
        value={status}
        onChange={(_, newValue) => {
          if (!newValue || newValue === status) {
            onBlurExit?.();
            return;
          }
          if (skipConfirm) {
            commitStatus(newValue);
            return;
          }
          setPendingStatus(newValue);
        }}
        onClose={() => {
          if (pendingStatus == null) {
            onBlurExit?.();
          }
        }}
        isOptionEqualToValue={(a, b) => a === b}
        getOptionLabel={(option) => getRFQStatusLabel(option, rfqStatuses)}
        renderOption={(props, option) => {
          const c = getRfqStatusColor(option, rfqStatuses);
          return (
            <li {...props} key={option}>
              <Chip
                size="small"
                label={getRFQStatusLabel(option, rfqStatuses)}
                sx={{
                  bgcolor: c.bg,
                  color: c.fg,
                  border: `1px solid ${c.border}`,
                  fontWeight: 600,
                  height: 22,
                }}
              />
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            fullWidth
            autoFocus={autoFocus}
            title={copyEnabled ? "Double-click to copy" : undefined}
            onDoubleClick={
              copyEnabled
                ? (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void copyTextToClipboard(statusLabel);
                  }
                : undefined
            }
            sx={{
              "& .MuiInputBase-root": {
                bgcolor: colors.bg,
                color: colors.fg,
                borderRadius: 1,
                px: 0.5,
                fontWeight: 600,
                border: `1px solid ${colors.border}`,
              },
              "& .MuiInputBase-input": {
                color: colors.fg,
                fontWeight: 600,
              },
            }}
          />
        )}
      />
      <ConfirmDialog
        open={pendingStatus != null}
        title="Change Status"
        okLabel="Change Status"
        onCancel={() => setPendingStatus(null)}
        onOk={() => {
          if (!pendingStatus) {
            return;
          }
          const next = pendingStatus;
          setPendingStatus(null);
          commitStatus(next);
        }}
      >
        Change status from {statusLabel} to {pendingLabel}?
      </ConfirmDialog>
    </Stack>
  );
};

export default RfqStatusAutocomplete;
